import { InputHTMLAttributes, ReactNode } from "react";
import classNames from "classnames";

export interface ButtonProps extends InputHTMLAttributes<HTMLButtonElement> {
  id?: string;
  className?: string;
  title: string;
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
  variant?:
    | "primary"
    | "outline"
    | "link"
    | "inline-link"
    | "plain"
    | "none"
    | "danger"
    | "outline-danger";
  color?: string;
  children?: ReactNode;
  preIcon?: ReactNode;
  postIcon?: ReactNode;
  disablePaddingX?: boolean;
}

export const Button = ({
  id,
  className,
  type = "button",
  onClick,
  variant = "primary",
  color,
  children,
  disabled,
  preIcon,
  postIcon,
  disablePaddingX = false,
  ...rest
}: ButtonProps) => {
  const baseStyle = "rounded text-center py-0.5 select-none";
  const blockStyle =
    variant === "inline-link" ? "inline-block" : "block w-full";
  const paddingXStyle = !disablePaddingX ? "px-4" : "";

  let variantStyle = "";
  const outlinedPassBtnStyle = () => {
    if (color) {
      return " text-gray-600 bg-transparent border border-dashed border-gray-600";
    }
    return variant != "none"
      ? " text-unleashed-pink bg-transparent border border-unleashed-pink"
      : "";
  };

  switch (variant) {
  case "primary":
    variantStyle +=
        " text-white bg-unleashed-pink border border-unleashed-pink";
    break;
  case "outline":
    variantStyle += outlinedPassBtnStyle();
    break;
  case "link":
  case "inline-link":
    variantStyle +=
        " text-unleashed-pink bg-transparent border border-transparent";
    break;
  case "danger":
    variantStyle += " text-white bg-red-600 border border-red-600";
    break;
  case "outline-danger":
    variantStyle += " text-danger bg-transparent border border-red-600";
    break;
  }

  const disabledStyle = disabled
    ? "cursor-not-allowed opacity-75 disabled:text-gray-400 disabled:bg-gray-200 disabled:border-gray-200"
    : "";

  const containerClasses = () => {
    if (variant === "inline-link") {
      return "inline-block";
    }
    return classNames(className);
  };

  if (variant === "none") {
    return (
      <button
        id={id}
        className={classNames(className, disabledStyle)}
        type={type}
        onClick={onClick}
        disabled={disabled}
        {...rest}
      >
        {children}
      </button>
    );
  }

  return (
    <div className={containerClasses()}>
      <button
        id={id}
        className={classNames(
          baseStyle,
          blockStyle,
          paddingXStyle,
          variantStyle,
          disabledStyle,
          preIcon || postIcon ? "flex items-center" : ""
        )}
        style={
          variant === "primary"
            ? { backgroundColor: color, borderColor: color }
            : {}
        }
        type={type}
        onClick={onClick}
        disabled={disabled}
        {...rest}
      >
        {children}
      </button>
    </div>
  );
};
